import { auth } from '@/lib/firebase';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

export interface NodeTrackingRecord {
  prompt_id: string;
  user_id: string;
  user_email?: string;
  class_type: string;
  timestamp: string;
  inputs: Record<string, unknown>;
  workflow_json?: string | null;
}

export interface NodeStats {
  totalExecutions: number;
  uniqueUsers: number;
  uniqueNodes: number;
  topNodes: Array<{ class_type: string; count: number }>;
  topUsers: Array<{ user_email: string; count: number }>;
  topModels: Array<{ model: string; count: number }>;
  hourlyActivity: Array<{ hour: string; count: number }>;
}

export interface NodeTrackingFilters {
  userEmail?: string;
  classType?: string;
  model?: string;
}

export interface FilterOptions {
  classTypes: string[];
  models: string[];
}

// ─── useNodeTrackingData ──────────────────────────────────────────────────────

interface NodeTrackingPage {
  records: NodeTrackingRecord[];
  total: number;
  hasNextPage: boolean;
  nextCursor: string | null;
  pageSize: number;
}

export const useNodeTrackingData = () => {
  const [data, setData] = useState<NodeTrackingRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(
    async (timeRange: string = '7d', cursor?: string, filters?: NodeTrackingFilters): Promise<void> => {
      const user = auth.currentUser;
      setLoading(true);
      setError(null);

      try {
        if (!user) throw new Error('User not authenticated');

        const idToken = await user.getIdToken();
        const response = await fetch('/api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({
            action: 'node-tracking-data',
            timeRange,
            cursor: cursor ?? undefined,
            filterUserEmail: filters?.userEmail || undefined,
            filterClassType: filters?.classType || undefined,
            filterModel: filters?.model || undefined,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to fetch node tracking data: ${response.status}`);
        }

        const result = await response.json();
        const page: NodeTrackingPage = result.data;

        setData(page.records || []);
        setTotal(page.total ?? 0);
        setHasNextPage(page.hasNextPage ?? false);
        setNextCursor(page.nextCursor ?? null);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        toast.error(`Failed to fetch node tracking data: ${msg}`);
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, total, hasNextPage, nextCursor, loading, error, fetchData };
};

// ─── useNodeTrackingStats ─────────────────────────────────────────────────────

export const useNodeTrackingStats = () => {
  const [stats, setStats] = useState<NodeStats>({
    totalExecutions: 0,
    uniqueUsers: 0,
    uniqueNodes: 0,
    topNodes: [],
    topUsers: [],
    topModels: [],
    hourlyActivity: Array.from({ length: 24 }, (_, i) => ({
      hour: String(i).padStart(2, '0') + ':00',
      count: 0,
    })),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async (timeRange: string = '7d'): Promise<void> => {
    const user = auth.currentUser;
    setLoading(true);
    setError(null);

    try {
      if (!user) throw new Error('User not authenticated');

      const idToken = await user.getIdToken();
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await fetch('/api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'node-tracking-stats', timeRange, userTimezone }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to fetch node tracking stats: ${response.status}`);
      }

      const result = await response.json();
      setStats(
        result.data || {
          totalExecutions: 0,
          uniqueUsers: 0,
          uniqueNodes: 0,
          topNodes: [],
          topUsers: [],
          topModels: [],
          hourlyActivity: Array.from({ length: 24 }, (_, i) => ({
            hour: String(i).padStart(2, '0') + ':00',
            count: 0,
          })),
        },
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to fetch node tracking stats: ${msg}`);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  return { stats, loading, error, fetchStats };
};

// ─── useNodeTrackingFilterOptions ─────────────────────────────────────────────

export const useNodeTrackingFilterOptions = () => {
  const [options, setOptions] = useState<FilterOptions>({ classTypes: [], models: [] });
  const [loading, setLoading] = useState(false);

  const fetchOptions = useCallback(async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user) return;
    setLoading(true);

    try {
      const idToken = await user.getIdToken();
      const response = await fetch('/api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'node-tracking-filter-options' }),
      });

      if (!response.ok) return;

      const result = await response.json();
      setOptions({
        classTypes: result.data?.classTypes ?? [],
        models: result.data?.models ?? [],
      });
    } catch {
      // silently ignore — filters just won't show dropdown options
    } finally {
      setLoading(false);
    }
  }, []);

  return { options, loading, fetchOptions };
};
